<crowdctrl-unauthenticated-page [pageTitle]="pageTitle" [showFooter]="false" defaultHref="/about">
  <div class="about-project features-page">
    <crowdctrl-unauth-page-intro [roomyBottom]="true">
      <h1 crowdctrlUnauthIntroTitle>Features</h1>
      <p crowdctrlUnauthIntroLead>
        Each app covers a different job: planning and governance, live sessions, public intake, and
        room displays.
      </p>
    </crowdctrl-unauth-page-intro>

    <section
      class="about-project__section about-project__section--four-apps"
      aria-labelledby="feat-apps"
    >
      <h2 id="feat-apps" class="about-project__heading">Four apps in each tenancy</h2>
      <p class="about-project__text features-apps-intro">
        Each tenancy includes CrowdCTRL Manager, Service, Forms, and Boards. Users, workspaces, and
        sessions in one tenancy are independent of all other tenancies.
      </p>
      <ul class="features-apps-grid">
        <li class="features-app-card">
          <ion-icon
            name="business-outline"
            class="features-app-card__icon"
            aria-hidden="true"
          ></ion-icon>
          <h3 class="features-app-card__title"><strong>CrowdCTRL Manager</strong></h3>
          <p class="features-app-card__text">
            Definitions for queues, forms, boards, access rules, and policies so new workspaces
            follow the same pattern across schools or faculties.
          </p>
        </li>
        <li class="features-app-card">
          <ion-icon
            name="briefcase-outline"
            class="features-app-card__icon"
            aria-hidden="true"
          ></ion-icon>
          <h3 class="features-app-card__title">
            <strong>CrowdCTRL Service</strong>
          </h3>
          <p class="features-app-card__text">
            For staff in the room: open workspaces and sessions, run queues, watch load, coordinate
            during teaching or lab time, and connect to institutional tools such as Canvas LMS.
          </p>
        </li>
        <li class="features-app-card">
          <ion-icon
            name="reader-outline"
            class="features-app-card__icon"
            aria-hidden="true"
          ></ion-icon>
          <h3 class="features-app-card__title"><strong>CrowdCTRL Forms</strong></h3>
          <p class="features-app-card__text">
            Public intake on tablets, phones, QR codes, and NFC. Manager and floor-operator tools
            stay out of this app, which suits kiosks and BYO devices.
          </p>
        </li>
        <li class="features-app-card">
          <ion-icon name="tv-outline" class="features-app-card__icon" aria-hidden="true"></ion-icon>
          <h3 class="features-app-card__title"><strong>CrowdCTRL Boards</strong></h3>
          <p class="features-app-card__text">
            Read-only displays for projectors and shared screens: queue state, timings, and
            announcements for the room.
          </p>
        </li>
      </ul>
    </section>

    <section class="features-section" aria-labelledby="feat-manager">
      <div class="features-section__header">
        <ion-icon
          name="business-outline"
          class="features-section__mega-icon"
          aria-hidden="true"
        ></ion-icon>
        <div class="features-section__headline">
          <h2 id="feat-manager" class="features-section__heading">CrowdCTRL Manager</h2>
        </div>
      </div>
      <ul class="about-project__list about-project__list--features">
        <li>
          Create <strong>workspace definitions</strong>: blueprints with queues, forms, boards,
          feature toggles, and <strong>configurable options</strong> so new workspaces start from a
          shared template, not a blank slate.
        </li>
        <li>
          Use <strong>visibility rules</strong> to control who can create workspace instances from a
          definition using group and role rules, keeping rollout consistent across faculties or
          campuses.
        </li>
        <li>
          Encode <strong>workspace and service behaviour</strong> in policies: queue order,
          <strong>aging</strong> for long waits, and optional <strong>escalations</strong>.
        </li>
        <li>
          <strong>Tenancy administrators</strong> invite people and manage users and groups within
          their tenancy.
        </li>
        <li>
          Stand up <strong>workspace instances</strong> and add <strong>members</strong> with
          workspace roles (<strong>Manager</strong>, <strong>Contributor</strong>, or
          <strong>Viewer</strong>) so teams can open sessions when classes run.
        </li>
      </ul>
    </section>

    <section class="features-section" aria-labelledby="feat-service">
      <div class="features-section__header">
        <ion-icon
          name="briefcase-outline"
          class="features-section__mega-icon"
          aria-hidden="true"
        ></ion-icon>
        <div class="features-section__headline">
          <h2 id="feat-service" class="features-section__heading">CrowdCTRL Service</h2>
        </div>
      </div>
      <ul class="about-project__list about-project__list--features">
        <li>
          Create workspace instances from definitions <strong>published</strong> to Service, so
          teams start from approved blueprints.
        </li>
        <li>
          <strong>Workspace members</strong> are <strong>Managers</strong>,
          <strong>Contributors</strong>, or <strong>Viewers</strong> for that workspace.
        </li>
        <li>
          In a <strong>live session</strong>, staff are <strong>Coordinators</strong>,
          <strong>Responders</strong>, or <strong>Observers</strong> for what they may do in that
          class. That usually follows their workspace role; a workspace can change the usual mapping
          when something different is required.
        </li>
        <li>
          <strong>Create and run sessions</strong> for a class or block: start, pause, resume, and
          finish when the block ends, with control over intake and service.
        </li>
        <li>
          Configure <strong>queues, forms, and boards</strong> within each definition’s limits; add
          more when the definition allows.
        </li>
        <li>
          Run queues and the session from <strong>Service</strong>, with
          <strong>forms and boards</strong> for that session managed from the same place.
          <strong>CrowdCTRL Forms</strong> and <strong>CrowdCTRL Boards</strong> are the public
          intake and display clients; coordinating the room and the queues stays in Service.
        </li>
        <li>
          <strong>Manual intake</strong> when a student has no device for QR, URL, or NFC. Staff add
          them to the queue from Service.
        </li>
        <li>
          <strong>Serving policies</strong> for responders: priorities, aging, and escalations
          across queues.
        </li>
        <li>
          <strong>Real-time load</strong> in Service: order counts, queue depth, and wait, service,
          and turnaround summaries at <strong>session and queue</strong> level.
        </li>
        <li>
          <strong>Session alerts</strong> when queue depth, wait time, or other metrics cross
          thresholds, with notifications to configured targets.
        </li>
        <li>
          <strong>Activity</strong>, <strong>search</strong>, and session tools so the team shares
          one view of the room during busy blocks.
        </li>
      </ul>

      <h3 class="features-section__subheading" id="feat-service-integrations">Integrations</h3>
      <ul class="about-project__list about-project__list--features">
        <li>
          <strong>Canvas LMS:</strong> match a <strong>student ID</strong> to profile details
          including <strong>name</strong> for intake and callouts.
        </li>
        <li>
          <strong>NFC tags</strong> can carry student ID; with Canvas, staff can resolve name and
          profile fields so queues and callouts match the student.
        </li>
      </ul>
    </section>

    <section class="features-section" aria-labelledby="feat-forms">
      <div class="features-section__header">
        <ion-icon
          name="reader-outline"
          class="features-section__mega-icon"
          aria-hidden="true"
        ></ion-icon>
        <div class="features-section__headline">
          <h2 id="feat-forms" class="features-section__heading">CrowdCTRL Forms</h2>
        </div>
      </div>
      <ul class="about-project__list about-project__list--features">
        <li>
          <strong>Standalone intake.</strong> No staff or tenancy-admin screens, suited to kiosks
          and unattended tablets.
        </li>
        <li>
          Students join via <strong>URLs, QR codes, or NFC</strong> shared for that class or
          session, landing in the correct form and queue for the live block.
        </li>
        <li>
          <strong>Place in line</strong> updates and <strong>notifications</strong> when staff are
          ready or call someone forward.
        </li>
        <li>
          With <strong>NFC</strong>, <strong>student ID</strong>, and <strong>Canvas LMS</strong>,
          students can <strong>confirm their name and/or ID</strong> before joining the queue.
        </li>
        <li>
          Optional <strong>Brother label printing</strong> on the <strong>Android</strong> build for
          tickets or receipts.
        </li>
      </ul>
    </section>

    <section class="features-section" aria-labelledby="feat-boards">
      <div class="features-section__header">
        <ion-icon
          name="tv-outline"
          class="features-section__mega-icon"
          aria-hidden="true"
        ></ion-icon>
        <div class="features-section__headline">
          <h2 id="feat-boards" class="features-section__heading">CrowdCTRL Boards</h2>
        </div>
      </div>
      <ul class="about-project__list about-project__list--features">
        <li>
          <strong>Read-only display</strong> for projectors and shared screens, not an operator
          console. Queue state and messages stay readable at a distance.
        </li>
        <li>
          Shows <strong>live queue information</strong> for the class: how many people are waiting,
          how long waits are running, and other numbers your team chose for that board layout.
        </li>
        <li>
          <strong>Text-to-speech</strong> announcements and <strong>call tones</strong> when the
          board uses audio callouts.
        </li>
        <li>
          Rotate <strong>class announcements</strong> or messages on a schedule, alongside queue
          status.
        </li>
      </ul>
    </section>

    <section class="features-section" aria-labelledby="feat-web">
      <div class="features-section__header">
        <ion-icon
          name="globe-outline"
          class="features-section__mega-icon"
          aria-hidden="true"
        ></ion-icon>
        <div class="features-section__headline">
          <h2 id="feat-web" class="features-section__heading">Web &amp; access</h2>
        </div>
      </div>
      <ul class="about-project__list about-project__list--features">
        <li>
          Staff and admin workflows run in the <strong>browser</strong> on laptops and tablets, as a
          <strong>progressive web app</strong> (install optional). The same web build runs on phone.
        </li>
        <li><strong>Several identity providers</strong> can be configured side by side.</li>
        <li>Workspaces and sessions are scoped by membership.</li>
      </ul>
    </section>

    <a href="/about" class="about-project__back-link" (click)="unauthNav.forward($event, '/about')"
      >Back to About</a
    >
  </div>
</crowdctrl-unauthenticated-page>
